import { z } from "zod";
import { adminOperationsAnySchemaVersionSchema, adminPermissionSchema, adminReasonCodeSchema, adminRoleSchema, adminSafeIdentifierSchema, adminSha256Schema, adminStageSchema, adminUlidSchema, } from "./admin-operations-core.js";
import { adminSubjectTypeSchema } from "./admin-operations-cases.js";
// API Gateway v2 request IDs are opaque base64-like values and commonly end in
// padding (`=`). Keep this narrower than the general identifier contract while
// preserving the exact AWS request ID needed for incident correlation.
export const adminAwsRequestIdSchema = z
    .string()
    .min(1)
    .max(255)
    .regex(/^[A-Za-z0-9_-]+={0,2}$/u);
export const adminAuditActionSchema = z.enum([
    "SESSION_VIEWED",
    "HEALTH_VIEWED",
    "BUSINESS_OVERVIEW_VIEWED",
    "CUSTOMER_ACTIVITY_OVERVIEW_VIEWED",
    "OBSERVABILITY_ATTENTION_VIEWED",
    "BOOK_JOURNEY_VIEWED",
    "CUSTOMER_LIST_VIEWED",
    "CUSTOMER_DETAIL_VIEWED",
    "AI_OPERATIONS_VIEWED",
    "RELIABILITY_VIEWED",
    "AUTHORIZATION_DENIED",
    // A request that failed for any other reason: an outage, a conflict or a bad request
    // (2026-10-21). Before, every failure was recorded as a refusal.
    "REQUEST_FAILED",
    "OPERATOR_ACCESS_VIEWED",
    "OPERATOR_ACCESS_REQUESTED",
    "OPERATOR_ACCESS_APPROVED",
    "OPERATOR_ACCESS_APPLIED",
    "BUSINESS_PARTNER_INVITED",
    "BUSINESS_PARTNER_DISABLED",
    "SUPPORT_AGENT_INVITED",
    "SUPPORT_AGENT_DISABLED",
    "OWNER_ACCESS_GRANTED",
    "OWNER_ACCESS_DISABLED",
    "BREAK_GLASS_REQUESTED",
    "BREAK_GLASS_APPROVED",
    "CASE_CREATED",
    "CASES_LISTED",
    "CASE_VIEWED",
    "SUPPORT_TICKET_VIEWED",
    "SUPPORT_TICKET_BOOK_VIEWED",
    "SUPPORT_COURTESY_EMAIL_RETRIED",
    "SUPPORT_COURTESY_VIEWED",
    "SUPPORT_TICKETS_LISTED",
    "SUPPORT_TEAMMATES_LISTED",
    "SUPPORT_TICKET_ASSIGNED",
    "SUPPORT_TICKET_REVIEW_STARTED",
    "SUPPORT_TICKET_WAITING_FOR_CUSTOMER",
    "SUPPORT_TICKET_RESOLVED",
    "SUPPORT_TICKET_REOPENED",
    "SUPPORT_TICKET_TAKEN_OVER",
    "SUPPORT_TICKET_HANDED_OFF",
    "SUPPORT_TICKET_REPLIED",
    "SUPPORT_REPLY_DRAFT_SAVED",
    "SUPPORT_REPLY_DRAFT_VIEWED",
    "SUPPORT_TICKET_STAFF_NOTE_ADDED",
    "CASE_RESOLVED",
    "EVIDENCE_SEARCHED",
    "CUSTOMER_RESOLVED",
    "CUSTOMER_TIMELINE_VIEWED",
    "BOOKS_LISTED",
    "SUPPORT_GRANT_REQUESTED",
    "SUPPORT_DIAGNOSTICS_VIEWED",
    "GIFT_BATCH_PREVIEWED",
    "GIFT_BATCHES_LISTED",
    "GIFT_BATCH_CREATED",
    "VOUCHER_EMAIL_ISSUED",
    "GOODWILL_VOUCHER_ISSUED",
    "ACTION_PREVIEW_VIEWED",
    "ACCOUNT_ERASURE_VIEWED",
    "GIFT_EXPORT_ISSUED",
    "GIFT_EXPORT_RECOVERED",
    "GIFT_BATCH_HANDED_OVER",
    "COMMERCE_INVESTIGATED",
    "FULFILLMENT_VIEWED",
    // Case-scoped edition reads (I3, 2026-10-28): a book's edition summary, a five-minute link to
    // one exact file, and the counts of a print order's staff alerts.
    "STAFF_CASE_SUMMARY_VIEWED",
    "STAFF_CASE_FILE_OPENED",
    "STAFF_ALERT_HISTORY_VIEWED",
    "ACTION_PREVIEWED",
    "ACTION_APPROVED",
    "COMMAND_ACCEPTED",
    "COMMAND_COMPLETED",
    "AUDIT_VIEWED",
    "AUDIT_INTEGRITY_VIEWED",
    "DEPLOYMENT_READINESS_VERIFIED",
]);
export const adminAuditEventSchema = z
    .object({
    schemaVersion: adminOperationsAnySchemaVersionSchema,
    eventId: adminUlidSchema,
    occurredAt: z.string().datetime(),
    stage: adminStageSchema,
    actorId: z.string().trim().min(1).max(128),
    effectiveRoles: z.array(adminRoleSchema).max(adminRoleSchema.options.length),
    effectivePermission: adminPermissionSchema.optional(),
    action: adminAuditActionSchema,
    outcome: z.enum(["SUCCEEDED", "DENIED", "FAILED"]),
    reasonCode: adminReasonCodeSchema,
    subjectType: adminSubjectTypeSchema,
    subjectId: adminSafeIdentifierSchema,
    caseId: adminUlidSchema.optional(),
    commandId: adminUlidSchema.optional(),
    approvalId: adminUlidSchema.optional(),
    targetVersion: z.number().int().nonnegative().optional(),
    beforeDigest: adminSha256Schema.optional(),
    afterDigest: adminSha256Schema.optional(),
    /**
     * The opaque ids a search or lookup returned, bounded to the page shown: never an email or
     * the text searched for (security review, 2026-11-02).
     */
    viewedSubjectIds: z
        .array(adminSafeIdentifierSchema.refine((id) => !id.includes("@")))
        .min(1)
        .max(25)
        .optional(),
    correlationId: z.string().uuid(),
    requestId: adminAwsRequestIdSchema,
    // ADMIN_OPERATOR_SCRIPT: a reviewed one-time script, such as closing stale cases (G3).
    source: z.enum([
        "ADMIN_API",
        "ADMIN_COMMAND_WORKER",
        "ADMIN_DEPLOYMENT_CANARY",
        "ADMIN_OPERATOR_SCRIPT",
    ]),
})
    .strict();
export const adminAuditOutboxSchema = z
    .object({
    schemaVersion: adminOperationsAnySchemaVersionSchema,
    eventId: adminUlidSchema,
    occurredAt: z.string().datetime(),
    state: z.literal("PENDING_ARCHIVE"),
    archivePayload: z.string().min(2).max(16_384),
})
    .strict();
export const adminAuditSearchCommandSchema = z
    .object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    actorId: z.string().min(1).max(128).optional(),
    action: adminAuditActionSchema.optional(),
    outcome: z.enum(["SUCCEEDED", "DENIED", "FAILED"]).optional(),
    subjectType: adminSubjectTypeSchema.optional(),
    subjectId: adminSafeIdentifierSchema.optional(),
    correlationId: z.string().uuid().optional(),
    limit: z.number().int().positive().max(100).default(50),
    /** The next, older page: events before this point (`occurredAt#eventId`, 2026-10-21). */
    before: z
        .string()
        .max(80)
        .regex(/^\d{4}-\d{2}-\d{2}T[0-9:.]+Z#[0-9A-Z]{26}$/u)
        .optional(),
})
    .strict()
    .refine((query) => Date.parse(query.to) > Date.parse(query.from), {
    path: ["to"],
    message: "Audit range must move forward.",
});
export const adminAuditSearchResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: z.literal("2026-09-23.2"),
        events: z.array(adminAuditEventSchema).max(100),
        truncated: z.boolean(),
        /** When more may exist: the `before` of the next, older page (2026-10-21). */
        nextBefore: z.string().max(80).optional(),
    })
        .strict(),
})
    .strict();
export const adminArchiveIntegrityResponseSchema = z
    .object({
    data: z
        .object({
        schemaVersion: z.literal("2026-09-23.2"),
        state: z.enum(["HEALTHY", "DEGRADED", "ATTENTION_REQUIRED"]),
        checkedAt: z.string().datetime(),
        pendingArchiveMessages: z.number().int().nonnegative(),
        inFlightArchiveMessages: z.number().int().nonnegative(),
        failedArchiveMessages: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=admin-operations-audit.js.map